import { encodePolyline, initials, simplifyToPointBudget } from '@motogo/shared';
import { BookmarkPlus, History, LogIn, Mic, MicOff, Phone, PhoneOff, Radio, Volume2, VolumeX, X } from 'lucide-react';
import { useEffect, useState, type FC, type FormEvent } from 'react';
import { createPortal } from 'react-dom';

import { useAuth } from '@/hooks/useAuth';
import { useRecentChannels } from '@/hooks/useRecentChannels';
import {
  buildLoadedRouteSaveInput,
  loadedRouteCoordinates,
  loadedRouteDurationSeconds,
  useRoutePlan,
} from '@/hooks/useRoutePlan';
import { useVoice } from '@/hooks/useVoice';
import { putLocalRoute } from '@/lib/routeStore';
import { saveRoute, updateRouteMeta } from '@/lib/savedRoutes';

// Inline shares carry the route's line through the hub — thinned so the encoded polyline
// stays well under SignalR's 32 KB receive cap (~2 000 points ≈ 12 KB); on the map the
// difference from the router-exact line is invisible.
const INLINE_SHARE_MAX_POINTS = 2000;

/**
 * The label this rider shows to others in the channel. Prefer the account's real name;
 * but OAuth stores the email as the name when the provider gives no display name, so if the
 * name is missing or is itself an email, fall back to the email's local part (e.g. "chris.b")
 * rather than broadcasting the full address.
 */
function riderName(name: string, email: string): string {
  const trimmed = name.trim();
  if (trimmed && !trimmed.includes('@')) return trimmed;
  return (trimmed || email).split('@')[0];
}

const VoiceControls: FC = () => {
  const { status, peers, muted, error, join, leave, toggleMute, togglePeerMute, shareRoute, shareRouteInline } =
    useVoice();
  const { state, exitGuestMode } = useAuth();
  const { activeRoute, setActiveRoute, loadedRoute } = useRoutePlan();
  // Voice is sign-in-only (the hub is [Authorize]-gated; guests have no server identity).
  // The name shown for "(you)" — peers' names come from the server now.
  const isGuest = state.status === 'guest';
  const displayName =
    state.status === 'authenticated' ? riderName(state.user.name, state.user.email) : 'Rider';

  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState('');
  const { recentChannels, remember, forget } = useRecentChannels();

  // Push the route currently on the map to the channel — the same flow as the route
  // detail page's "Send to channel", surfaced here so it's one tap while in a channel.
  // A *saved* route (activeRoute) shares by id after the private→link bump; an *unsaved*
  // one (loadedRoute: planner result / GPX / received share) first offers the choice
  // between saving it (full detail, id share) and sending its thinned line inline.
  const [confirmShare, setConfirmShare] = useState(false);
  const [chooseShare, setChooseShare] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareDone, setShareDone] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  // Reset the confirm/choice steps whenever the sheet closes so they don't linger armed —
  // and clear a stale share error so reopening the sheet later doesn't show an old failure.
  useEffect(() => {
    if (!open) {
      setConfirmShare(false);
      setChooseShare(false);
      setShareError(null);
    }
  }, [open]);

  const finishShare = () => {
    setShareDone(true);
    setTimeout(() => setShareDone(false), 2500);
  };

  const shareCurrentRoute = async () => {
    if (!activeRoute || shareBusy) return;
    // Sharing needs the route link-visible (receivers fetch it by id); confirm the bump.
    if (activeRoute.visibility === 'private' && !confirmShare) {
      setConfirmShare(true);
      return;
    }
    setShareBusy(true);
    setShareError(null);
    setConfirmShare(false);
    try {
      if (activeRoute.visibility === 'private') {
        const updated = await updateRouteMeta(activeRoute.id, activeRoute.name, activeRoute.description, 'link');
        if (!updated) throw new Error('Could not make the route shareable.');
        await putLocalRoute(updated, true).catch(() => undefined);
        setActiveRoute({ ...activeRoute, visibility: 'link' });
      }
      const sent = await shareRoute(activeRoute.id, activeRoute.name);
      if (!sent) throw new Error('Not connected to a channel.');
      finishShare();
    } catch (e) {
      setShareError(e instanceof Error ? e.message : 'Sharing failed');
    } finally {
      setShareBusy(false);
    }
  };

  /** "Save & send": save the unsaved route (default name), bump it link-visible, share by id. */
  const saveAndShare = async () => {
    if (!loadedRoute || shareBusy) return;
    setShareBusy(true);
    setShareError(null);
    setChooseShare(false);
    try {
      const input = buildLoadedRouteSaveInput(loadedRoute, loadedRoute.name);
      if (!input) throw new Error('There is no route to share.');
      const saved = await saveRoute(input);
      if (!saved) throw new Error('Sign in to save routes.');
      await putLocalRoute(saved, true).catch(() => undefined);
      const linked = await updateRouteMeta(saved.id, saved.name, saved.description, 'link');
      if (!linked) throw new Error('Could not make the route shareable.');
      await putLocalRoute(linked, true).catch(() => undefined);
      // It's a saved route on the map now — later shares go straight through the id path.
      setActiveRoute({ id: linked.id, name: linked.name, description: linked.description, visibility: 'link' });
      const sent = await shareRoute(linked.id, linked.name);
      if (!sent) throw new Error('Not connected to a channel.');
      finishShare();
    } catch (e) {
      setShareError(e instanceof Error ? e.message : 'Sharing failed');
    } finally {
      setShareBusy(false);
    }
  };

  /** "Send without saving": push the thinned line itself through the hub — no server row. */
  const shareInline = async () => {
    if (!loadedRoute || shareBusy) return;
    setShareBusy(true);
    setShareError(null);
    setChooseShare(false);
    try {
      const coordinates = loadedRouteCoordinates(loadedRoute);
      if (coordinates.length < 2) throw new Error('There is no route to share.');
      const thinned = simplifyToPointBudget(coordinates, INLINE_SHARE_MAX_POINTS);
      const polyline = encodePolyline(thinned.map(([lon, lat]) => [lat, lon] as [number, number]));
      const sent = await shareRouteInline(loadedRoute.name, polyline, loadedRouteDurationSeconds(loadedRoute));
      if (!sent) throw new Error('Not connected to a channel.');
      finishShare();
    } catch (e) {
      setShareError(e instanceof Error ? e.message : 'Sharing failed');
    } finally {
      setShareBusy(false);
    }
  };

  const active = status === 'connected' || status === 'connecting' || status === 'reconnecting';
  // Amber while reconnecting: still in the channel, but degraded — glanceable on the bar.
  const color =
    error ? 'var(--danger)' : status === 'reconnecting' ? 'var(--warning)' : active ? 'var(--success)' : 'var(--accent)';

  // Close any open dialog with Escape, mirroring SettingsModal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const joinChannel = (name: string) => {
    remember(name);
    void join(name);
    setChannel('');
    setOpen(false);
  };

  const handleJoin = (e: FormEvent) => {
    e.preventDefault();
    const name = channel.trim();
    if (!name) return;
    joinChannel(name);
  };

  const handleLeave = () => {
    void leave();
    setOpen(false);
  };

  return (
    <>
      <button
        className="mg-icon-btn"
        style={{ color }}
        onClick={() => setOpen(true)}
        title={error ?? (active ? 'Voice channel' : 'Join voice')}
        aria-label={active ? 'Voice channel' : 'Join voice'}
      >
        {active ? <PhoneOff size={26} /> : <Phone size={26} />}
        {peers.length > 0 && (
          <small style={{ fontSize: '0.65rem', fontWeight: 700, lineHeight: 1, marginTop: 2 }}>
            {peers.length}
          </small>
        )}
      </button>

      {/* Portal to <body>: the bottom bar's backdrop-filter makes it the containing
          block for fixed-position descendants, which would clip this overlay to the bar. */}
      {open && createPortal(
        <div className="mg-backdrop" onClick={() => setOpen(false)}>
          <div
            className="mg-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={active ? 'Voice channel' : 'Join voice'}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mg-sheet-handle" />
            <div className="mg-sheet-header">
              <div className="mg-sheet-title">{active ? 'Voice channel' : 'Join voice'}</div>
              <button className="mg-icon-btn" onClick={() => setOpen(false)} title="Close" aria-label="Close">
                <X size={22} />
              </button>
            </div>

            {active ? (
              <>
                {status === 'reconnecting' && (
                  <div
                    style={{
                      color: 'var(--warning)',
                      fontSize: '0.9rem',
                      marginBottom: 10,
                      flexShrink: 0,
                    }}
                  >
                    Connection lost — reconnecting…
                  </div>
                )}

                <div className="mg-participants">
                  {/* Local rider first — the mic toggle controls our own outgoing audio */}
                  <div className="mg-participant">
                    <span className="mg-participant-name" style={{ fontWeight: 600 }}>
                      <span className="mg-avatar">{initials(displayName)}</span>
                      {displayName} (you)
                    </span>
                    <button
                      className="mg-icon-btn"
                      style={{ color: muted ? 'var(--danger)' : 'var(--success)' }}
                      onClick={toggleMute}
                      title={muted ? 'Unmute your microphone' : 'Mute your microphone'}
                      aria-label={muted ? 'Unmute your microphone' : 'Mute your microphone'}
                    >
                      {muted ? <MicOff size={22} /> : <Mic size={22} />}
                    </button>
                  </div>

                  {/* Each peer: the speaker toggle silences THEIR audio for us only */}
                  {peers.map((p) => (
                    <div key={p.id} className="mg-participant" style={{ color: 'var(--text-dim)' }}>
                      <span className="mg-participant-name">
                        <span className="mg-avatar">{initials(p.name)}</span>
                        {p.name}
                      </span>
                      <button
                        className="mg-icon-btn"
                        style={{ color: p.muted ? 'var(--danger)' : 'var(--success)' }}
                        onClick={() => togglePeerMute(p.id)}
                        title={p.muted ? `Unmute ${p.name} for you` : `Mute ${p.name} for you`}
                        aria-label={p.muted ? `Unmute ${p.name} for you` : `Mute ${p.name} for you`}
                      >
                        {p.muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                      </button>
                    </div>
                  ))}

                  {peers.length === 0 && (
                    <div style={{ color: 'var(--text-dim)', fontSize: '0.9rem', padding: '4px 0' }}>
                      {status === 'connecting' ? 'Connecting…' : 'Waiting for others to join…'}
                    </div>
                  )}
                </div>

                {/* Push the route on the map to everyone in the channel — shortcut for the
                    route detail page's "Send to channel". Only when actually connected.
                    Works for any route: saved ones share by id, unsaved ones (planned /
                    GPX / received) offer save-first or inline. */}
                {status === 'connected' && (activeRoute || loadedRoute) && (
                  chooseShare && loadedRoute && !activeRoute ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16, flexShrink: 0 }}>
                      <div style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                        This route isn&rsquo;t in your library yet.
                      </div>
                      <button
                        className="mg-btn mg-btn-primary"
                        onClick={() => void saveAndShare()}
                        disabled={shareBusy}
                      >
                        <BookmarkPlus size={18} />
                        Save & send — keeps full detail
                      </button>
                      <button className="mg-btn mg-btn-outline" onClick={() => void shareInline()} disabled={shareBusy}>
                        <Radio size={18} />
                        Send without saving
                      </button>
                      <button className="mg-link-btn" onClick={() => setChooseShare(false)}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="mg-btn mg-btn-outline"
                      style={{ marginTop: 16, flexShrink: 0 }}
                      onClick={() => (activeRoute ? void shareCurrentRoute() : setChooseShare(true))}
                      disabled={shareBusy}
                      title={`Share ${(activeRoute ?? loadedRoute)!.name} with the channel`}
                    >
                      <Radio size={18} />
                      {shareDone
                        ? 'Route sent to channel'
                        : confirmShare
                          ? 'Tap again — this makes the route link-visible'
                          : shareBusy
                            ? 'Sharing…'
                            : `Share "${(activeRoute ?? loadedRoute)!.name}"`}
                    </button>
                  )
                )}
                {status === 'connected' && (activeRoute || loadedRoute) && shareError && (
                  <div style={{ color: 'var(--danger)', fontSize: '0.9rem', marginTop: 8, flexShrink: 0 }}>
                    {shareError}
                  </div>
                )}

                <button
                  className="mg-btn mg-btn-danger"
                  style={{ marginTop: 16, flexShrink: 0 }}
                  onClick={handleLeave}
                >
                  <PhoneOff size={18} />
                  Leave channel
                </button>
              </>
            ) : isGuest ? (
              // Voice + location sharing need an account: peers are keyed by user identity
              // on the server. The rest of the app stays guest-accessible.
              <>
                <p style={{ color: 'var(--text-dim)', fontSize: '0.95rem', margin: '4px 0 16px' }}>
                  Voice channels and live group locations need an account, so your riding
                  group can recognise you. Sign in to join — it only takes a moment.
                </p>
                <button className="mg-btn mg-btn-primary" onClick={() => exitGuestMode()}>
                  <LogIn size={18} />
                  Sign in to use voice
                </button>
              </>
            ) : (
              <>
                {/* One-tap rejoin — retyping a channel name with gloves on is the worst
                    input this app asks for, so recents come before the free-text field. */}
                {recentChannels.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 4, color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                      Recent channels
                    </div>
                    {recentChannels.map((c) => (
                      <div key={c} className="mg-participant">
                        <button
                          className="mg-recent-btn"
                          onClick={() => joinChannel(c)}
                          title={`Join ${c}`}
                        >
                          <History size={18} />
                          {c}
                        </button>
                        <button
                          className="mg-icon-btn"
                          style={{ color: 'var(--text-dim)' }}
                          onClick={() => forget(c)}
                          title={`Forget ${c}`}
                          aria-label={`Forget ${c}`}
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <form onSubmit={handleJoin}>
                  <label
                    htmlFor="voice-channel"
                    style={{ display: 'block', marginBottom: 8, color: 'var(--text-dim)', fontSize: '0.9rem' }}
                  >
                    Channel name
                  </label>
                  <input
                    id="voice-channel"
                    className="mg-input"
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    placeholder="e.g. sunday-ride"
                    autoFocus
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    className="mg-btn mg-btn-primary"
                    style={{ marginTop: 12 }}
                    disabled={!channel.trim()}
                  >
                    Join
                  </button>
                </form>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default VoiceControls;
